import React, { useState } from "react";
import { ImagePlus, X, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImagePicker from "@/components/media/ImagePicker";
import { imgUrl } from "@/pages/curated/shared";

export function CuratedSettingsTab({ trip, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const emails = trip.notify_emails || [];
  const addEmail = () => {
    const v = emailInput.trim().toLowerCase();
    if (!/\S+@\S+\.\S+/.test(v)) return;
    if (!emails.includes(v)) onChange({ notify_emails: [...emails, v] });
    setEmailInput("");
  };
  return (
    <div className="grid gap-5 lg:grid-cols-[2fr_1fr]" data-testid="curated-settings-tab">
      <div className="space-y-5">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Presentación</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Destino</Label><Input value={trip.destination || ""} onChange={(e) => onChange({ destination: e.target.value })} placeholder="Italy · Piedmont & Lake Como" data-testid="curated-destination-input" /></div>
            <div><Label>Tagline (marketing)</Label><Input value={trip.tagline || ""} onChange={(e) => onChange({ tagline: e.target.value })} placeholder="A gentle 8-day escape through Piedmont wineries and Lake Como" data-testid="curated-tagline-input" /></div>
            <div><Label>Descripción (introducción en la landing)</Label><Textarea rows={5} value={trip.description || ""} onChange={(e) => onChange({ description: e.target.value })} placeholder="Historia del viaje, tono, qué encontrarán los viajeros…" data-testid="curated-description-input" /></div>
            <div>
              <Label>Imagen principal (hero)</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input value={trip.hero_image || ""} onChange={(e) => onChange({ hero_image: e.target.value })} placeholder="Pega una URL o elige del banco de imágenes" data-testid="curated-hero-input" />
                <Button type="button" variant="outline" onClick={() => setPickerOpen(true)} data-testid="curated-hero-pick"><ImagePlus className="mr-1.5 h-4 w-4" /> Elegir</Button>
              </div>
              {trip.hero_image && <div className="mt-2 aspect-[16/6] overflow-hidden rounded-md border bg-muted"><img src={imgUrl(trip.hero_image)} alt="" className="h-full w-full object-cover" /></div>}
              <ImagePicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={(u) => onChange({ hero_image: u })} title="Imagen principal" defaultQuery={trip.destination || ""} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-5">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Fechas & cupo</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Inicio</Label><Input type="date" value={trip.start_date || ""} onChange={(e) => onChange({ start_date: e.target.value })} data-testid="curated-start-input" /></div>
              <div><Label>Fin</Label><Input type="date" value={trip.end_date || ""} onChange={(e) => onChange({ end_date: e.target.value })} data-testid="curated-end-input" /></div>
            </div>
            <div>
              <Label>Cupo total (plazas)</Label>
              <Input type="number" min={1} max={100} value={trip.capacity} onChange={(e) => onChange({ capacity: Number(e.target.value) || 0 })} data-testid="curated-capacity-input" />
              <p className="mt-1 text-xs text-muted-foreground">{trip.seats_committed || 0} comprometidas · {Math.max(0, trip.capacity - (trip.seats_committed || 0))} libres</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Precios por persona</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Moneda de cobro</Label>
              <Select value={trip.currency || "USD"} onValueChange={(v) => onChange({ currency: v })}><SelectTrigger data-testid="curated-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="USD">USD ($)</SelectItem><SelectItem value="EUR">EUR (€)</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Habitación individual</Label><Input type="number" min={0} value={trip.price_single} onChange={(e) => onChange({ price_single: Number(e.target.value) || 0 })} data-testid="curated-price-single-input" /></div>
            <div><Label>Habitación doble (compartida)</Label><Input type="number" min={0} value={trip.price_double} onChange={(e) => onChange({ price_double: Number(e.target.value) || 0 })} data-testid="curated-price-double-input" /></div>
            <div><Label>Depósito (%)</Label><Input type="number" min={5} max={100} value={trip.deposit_pct ?? 30} onChange={(e) => onChange({ deposit_pct: Number(e.target.value) || 30 })} data-testid="curated-deposit-input" /></div>
            <p className="text-xs leading-relaxed text-muted-foreground">Mismos plazos que el Creador: depósito si faltan &gt; 60 días, saldo antes de 60 días de la salida; <strong>100 %</strong> obligatorio si faltan ≤ 60 días.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Avisos de reserva</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex gap-2">
              <Input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }} placeholder="email@espiritutravel.com" data-testid="curated-notify-input" />
              <Button type="button" variant="outline" size="icon" onClick={addEmail} data-testid="curated-notify-add" aria-label="Añadir"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {emails.map((e) => (
                <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`curated-notify-${e}`}>{e}
                  <button type="button" onClick={() => onChange({ notify_emails: emails.filter((x) => x !== e) })} aria-label="Quitar"><X className="h-3 w-3" /></button></Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Reciben aviso in-app (y email cuando el envío esté activo) al capturarse un pago.{emails.length === 0 && <span className="block text-amber-700">Sin destinatarios: nadie recibirá el aviso.</span>}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Link público</CardTitle></CardHeader>
          <CardContent>
            {trip.status === "draft" ? <p className="text-xs italic text-muted-foreground">Publica el viaje para activar el link.</p>
              : <p className="break-all rounded bg-muted p-2 font-mono text-xs" data-testid="curated-public-url">{`${window.location.origin}/curated/${trip.public_token}`}</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
